import { OVERVIEW_TILES } from '../overview-tiles.ts';
import type { DashboardWidget, WidgetKind, WidgetSize } from './widget-types.ts';

export type WidgetPreset = {
  readonly key: string;
  readonly kind: WidgetKind;
  readonly size: WidgetSize;
  readonly settings: DashboardWidget['settings'];
};

export type PresetGroup = 'indicators' | 'trends' | 'breakdowns' | 'orders';

type KpiTileKey = Extract<DashboardWidget, { kind: 'kpi' }>['settings']['tile'];

const kpiPresets: readonly WidgetPreset[] = OVERVIEW_TILES.map((tile) => ({
  key: `kpi-${tile.key}`,
  kind: 'kpi',
  size: 'sm',
  settings: { tile: tile.key as KpiTileKey },
}));

export const WIDGET_PRESETS: Readonly<Record<PresetGroup, readonly WidgetPreset[]>> = {
  indicators: kpiPresets,
  trends: [
    {
      key: 'salesTrend',
      kind: 'trend',
      size: 'lg',
      settings: { measure: 'sales', chart: 'area', group: 'auto' },
    },
    {
      key: 'ordersTrend',
      kind: 'trend',
      size: 'lg',
      settings: { measure: 'created', chart: 'line', group: 'auto' },
    },
    {
      key: 'returnsTrend',
      kind: 'trend',
      size: 'md',
      settings: { measure: 'returned', chart: 'columns', group: 'week' },
    },
  ],
  breakdowns: [
    {
      key: 'deliveryRateByCarrier',
      kind: 'breakdown',
      size: 'lg',
      settings: { dimension: 'carrier', measure: 'deliveryRate', chart: 'line', group: 'week' },
    },
    {
      key: 'ordersByCarrier',
      kind: 'breakdown',
      size: 'lg',
      settings: { dimension: 'carrier', measure: 'orders', chart: 'grouped', group: 'auto' },
    },
    {
      key: 'statusesByDay',
      kind: 'breakdown',
      size: 'full',
      settings: { dimension: 'status', measure: 'orders', chart: 'stacked', group: 'auto' },
    },
    {
      key: 'salesBySource',
      kind: 'breakdown',
      size: 'md',
      settings: { dimension: 'source', measure: 'sales', chart: 'donut', group: 'auto' },
    },
    {
      key: 'ordersByGovernorate',
      kind: 'breakdown',
      size: 'md',
      settings: { dimension: 'governorate', measure: 'orders', chart: 'barList', group: 'auto' },
    },
  ],
  orders: [
    { key: 'funnel', kind: 'funnel', size: 'md', settings: {} },
    {
      key: 'topProducts',
      kind: 'topProducts',
      size: 'md',
      settings: { rankedBy: 'units', size: 10, chart: 'barList' },
    },
  ],
};

export const widgetFromPreset = (preset: WidgetPreset, id: string): DashboardWidget =>
  ({ id, kind: preset.kind, size: preset.size, title: null, settings: preset.settings }) as DashboardWidget;
